import { SolarTime } from "tyme4ts";
import {
  computeBranchRelation,
  computeElementRelation,
  pickDailyFocusArea,
  sectionVariantIndex,
} from "./chineseZodiacInteractions.js";

const CHINESE_ZODIAC_TO_ENGLISH = {
  鼠: "Rat",
  牛: "Ox",
  虎: "Tiger",
  兔: "Rabbit",
  龙: "Dragon",
  蛇: "Snake",
  马: "Horse",
  羊: "Goat",
  猴: "Monkey",
  鸡: "Rooster",
  狗: "Dog",
  猪: "Pig",
};

const CHINESE_ELEMENT_TO_ENGLISH = {
  木: "Wood",
  火: "Fire",
  土: "Earth",
  金: "Metal",
  水: "Water",
};

const LEGACY_ELEM_CYCLE = ["Wood", "Wood", "Fire", "Fire", "Earth", "Earth", "Metal", "Metal", "Water", "Water"];
const LEGACY_HOUR_ANIMALS = [10, 10, 11, 11, 0, 0, 1, 1, 2, 2, 3, 3, 4, 4, 5, 5, 6, 6, 7, 7, 8, 8, 9, 9];
const LEGACY_CZ_CHINESE = ["子", "丑", "寅", "卯", "辰", "巳", "午", "未", "申", "酉", "戌", "亥"];

const GUIDANCE_VARIANT_COUNT = 8;
const TONE_KEYS = [
  "cz_daily_tone_steady_progress",
  "cz_daily_tone_quiet_confidence",
  "cz_daily_tone_gentle_courage",
  "cz_daily_tone_disciplined_focus",
];

const ELEMENT_BALANCE_KEYS = {
  Wood: "cz_synthesis_balance_element_wood",
  Fire: "cz_synthesis_balance_element_fire",
  Earth: "cz_synthesis_balance_element_earth",
  Metal: "cz_synthesis_balance_element_metal",
  Water: "cz_synthesis_balance_element_water",
};

export const SDK_ENGINE = "tyme4ts";
export const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const HOUR_PERIODS = ["23:00-01:00", "01:00-03:00", "03:00-05:00", "05:00-07:00", "07:00-09:00", "09:00-11:00", "11:00-13:00", "13:00-15:00", "15:00-17:00", "17:00-19:00", "19:00-21:00", "21:00-23:00"];

function getHourPeriodIndex(hour) {
  const normalizedHour = ((Number(hour) % 24) + 24) % 24;
  return Math.floor((normalizedHour + 1) / 2) % 12;
}

function getAnimalByChineseZodiac(czData, zodiacName) {
  const englishName = CHINESE_ZODIAC_TO_ENGLISH[zodiacName];
  return czData.find((item) => item.name === englishName) || czData[0];
}

function getElementByChineseName(elementName, fallback) {
  return CHINESE_ELEMENT_TO_ENGLISH[elementName] || fallback || "Earth";
}

function monthAnimal(month) {
  const order = [11, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  return order[month - 1];
}

function yearElement(year) {
  const idx = ((year - 1924) % 10 + 10) % 10;
  return LEGACY_ELEM_CYCLE[idx];
}

function monthElement(month, year) {
  const base = ((year - 1924) * 12 + (month - 1)) % 10;
  return LEGACY_ELEM_CYCLE[(base % 10 + 10) % 10];
}

function dayAnimal(year, month, day) {
  const a = Math.floor((14 - month) / 12);
  const y = year + 4800 - a;
  const m = month + 12 * a - 3;
  const jdn = day + Math.floor((153 * m + 2) / 5) + 365 * y + Math.floor(y / 4) - Math.floor(y / 100) + Math.floor(y / 400) - 32045;
  return (jdn % 12 + 12) % 12;
}

function dayElement(year, month, day) {
  const a = Math.floor((14 - month) / 12);
  const y = year + 4800 - a;
  const m = month + 12 * a - 3;
  const jdn = day + Math.floor((153 * m + 2) / 5) + 365 * y + Math.floor(y / 4) - Math.floor(y / 100) + Math.floor(y / 400) - 32045;
  return LEGACY_ELEM_CYCLE[(jdn % 10 + 10) % 10];
}

function hourElement(hour, dayAnimalIdx) {
  const base = (dayAnimalIdx * 2 + Math.floor(hour / 2)) % 10;
  return LEGACY_ELEM_CYCLE[(base + 10) % 10];
}

function computeBaZiLegacy(year, month, day, hour, czData, pillarRoles, pillarDescriptions) {
  const yAnimalIdx = ((year - 1924) % 12 + 12) % 12;
  const mAnimalIdx = monthAnimal(month);
  const dAnimalIdx = dayAnimal(year, month, day);
  const hAnimalIdx = LEGACY_HOUR_ANIMALS[hour];
  return [
    { animal: czData[yAnimalIdx], element: yearElement(year), chinese: LEGACY_CZ_CHINESE[yAnimalIdx], period: `${year}`, role: pillarRoles[0], desc: pillarDescriptions[0] },
    { animal: czData[mAnimalIdx], element: monthElement(month, year), chinese: LEGACY_CZ_CHINESE[mAnimalIdx], period: MONTHS[month - 1], role: pillarRoles[1], desc: pillarDescriptions[1] },
    { animal: czData[dAnimalIdx], element: dayElement(year, month, day), chinese: LEGACY_CZ_CHINESE[dAnimalIdx], period: `Day ${day}`, role: pillarRoles[2], desc: pillarDescriptions[2] },
    { animal: czData[hAnimalIdx], element: hourElement(hour, dAnimalIdx), chinese: LEGACY_CZ_CHINESE[hAnimalIdx], period: HOUR_PERIODS[getHourPeriodIndex(hour)], role: pillarRoles[3], desc: pillarDescriptions[3] },
  ];
}

function computeBaZiWithTyme(year, month, day, hour, czData, pillarRoles, pillarDescriptions) {
  const eightChar = SolarTime.fromYmdHms(year, month, day, hour, 0, 0).getLunarHour().getEightChar();
  const cycles = [eightChar.getYear(), eightChar.getMonth(), eightChar.getDay(), eightChar.getHour()];
  return cycles.map((cycle, index) => {
    const animal = getAnimalByChineseZodiac(czData, cycle.getEarthBranch().getZodiac().toString());
    const chinese = cycle.getEarthBranch().toString();
    const element = getElementByChineseName(cycle.getHeavenStem().getElement().toString(), animal.element);
    const period = index === 0
      ? `${year}`
      : index === 1
        ? MONTHS[month - 1]
        : index === 2
          ? `Day ${day}`
          : HOUR_PERIODS[getHourPeriodIndex(hour)];
    return { animal, element, chinese, period, role: pillarRoles[index], desc: pillarDescriptions[index] };
  });
}

export function slugifySign(name) {
  return String(name || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
}

export function slugifyElement(element) {
  return slugifySign(element);
}

function interpolate(template, vars = {}) {
  return String(template || "").replace(/%\{(\w+)\}/g, (_, key) => (
    Object.prototype.hasOwnProperty.call(vars, key) ? String(vars[key]) : `%{${key}}`
  ));
}

export function todayIsoDate() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function normalizeIsoDate(value) {
  const match = String(value || "").match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
}

export function parseIsoDate(iso) {
  const normalized = normalizeIsoDate(iso);
  if (!normalized) return null;
  const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return null;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year, month, day };
}

export function computeBaZiProfile({ year, month, day, hour, czData, pillarRoles, pillarDescriptions }) {
  try {
    return computeBaZiWithTyme(year, month, day, hour, czData, pillarRoles, pillarDescriptions);
  } catch (_) {
    return computeBaZiLegacy(year, month, day, hour, czData, pillarRoles, pillarDescriptions);
  }
}

export function computeReadingDayPillar(readingYear, readingMonth, readingDay, czData) {
  try {
    const eightChar = SolarTime.fromYmdHms(readingYear, readingMonth, readingDay, 12, 0, 0).getLunarHour().getEightChar();
    const dayCycle = eightChar.getDay();
    const animal = getAnimalByChineseZodiac(czData, dayCycle.getEarthBranch().getZodiac().toString());
    const element = getElementByChineseName(dayCycle.getHeavenStem().getElement().toString(), animal.element);
    return { animal, element };
  } catch (_) {
    const dAnimalIdx = dayAnimal(readingYear, readingMonth, readingDay);
    return {
      animal: czData[dAnimalIdx],
      element: dayElement(readingYear, readingMonth, readingDay),
    };
  }
}

export function deterministicPick(seed, options) {
  if (!options?.length) return "";
  return options[Math.abs(seed) % options.length];
}

export function readingDateSeed(readingYear, readingMonth, readingDay) {
  return (readingYear * 10000) + (readingMonth * 100) + readingDay;
}

export function birthChartSeed(pillars) {
  if (!pillars?.length) return 0;

  return pillars.reduce((acc, pillar, index) => {
    const animal = pillar?.animal?.name || "";
    const element = pillar?.element || "";
    let part = 0;
    for (let i = 0; i < animal.length; i += 1) {
      part += animal.charCodeAt(i) * (index + 3);
    }
    for (let i = 0; i < element.length; i += 1) {
      part += element.charCodeAt(i) * (index + 7);
    }
    return acc + part;
  }, 0);
}

function findSignProfile(czData, animalName) {
  return czData.find((item) => item.name === animalName) || null;
}

function pickColorKey(seed, keyList, labelList) {
  if (keyList?.length) {
    const key = deterministicPick(seed, keyList);
    const index = keyList.indexOf(key);
    const label = index >= 0 && labelList?.[index] ? labelList[index] : key;
    return { key, label };
  }
  const label = deterministicPick(seed, labelList || []);
  return { key: null, label };
}

export function guidanceVariantIndex(dateSeed, chartSeed, sectionName, variantCount = GUIDANCE_VARIANT_COUNT) {
  return sectionVariantIndex(dateSeed, chartSeed, sectionName, variantCount);
}

export function resolveGuidanceLine(t, key, vars = {}, fallbackKey = null) {
  let template = t(key);
  if ((!template || template === key) && fallbackKey) {
    template = t(fallbackKey);
  }
  if (!template || template === key) return "";
  return interpolate(template, vars);
}

function buildGuidanceContext({
  pillars,
  readingDayPillar,
  anchorSign,
  focusPillar,
  dateSeed,
  chartSeed,
  getSignLabel,
  getElementLabel,
  t,
}) {
  const focusAnimalLabel = getSignLabel
    ? getSignLabel(focusPillar.animal.name)
    : (focusPillar.animal.label || focusPillar.animal.name);
  const focusElementLabel = getElementLabel
    ? getElementLabel(focusPillar.element)
    : focusPillar.element.toLowerCase();
  const readingAnimalLabel = getSignLabel
    ? getSignLabel(readingDayPillar.animal.name)
    : (readingDayPillar.animal.label || readingDayPillar.animal.name);
  const readingElementLabel = getElementLabel
    ? getElementLabel(readingDayPillar.element)
    : readingDayPillar.element.toLowerCase();
  const daySlug = slugifySign(readingDayPillar.animal.name);
  const birthElementSlug = slugifyElement(focusPillar.element);
  const dayElementSlug = slugifyElement(readingDayPillar.element);

  const branchRelation = computeBranchRelation(focusPillar.animal.name, readingDayPillar.animal.name);
  const elementRelation = computeElementRelation(readingDayPillar.element, focusPillar.element);
  const focusArea = pickDailyFocusArea(dateSeed + (chartSeed * 31));

  const compatSource = anchorSign?.compatible?.length ? anchorSign.compatible : [];
  const compatOffset = chartSeed % (compatSource.length || 1);
  const compatible1Name = compatSource[compatOffset % compatSource.length];
  const compatible2Name = compatSource[(compatOffset + 1) % compatSource.length];
  const compatible1 = getSignLabel ? getSignLabel(compatible1Name) : compatible1Name;
  const compatible2 = getSignLabel ? getSignLabel(compatible2Name) : compatible2Name;

  const elementChemistry = resolveGuidanceLine(
    t,
    `cz_daily_element_${dayElementSlug}_${birthElementSlug}`,
    {
      reading_element: readingElementLabel,
      reading_animal: readingAnimalLabel,
      element: focusElementLabel,
      animal: focusAnimalLabel,
    },
    "cz_daily_summary",
  );

  return {
    animal: focusAnimalLabel,
    element: focusElementLabel,
    reading_animal: readingAnimalLabel,
    reading_element: readingElementLabel,
    compatible1,
    compatible2,
    daySlug,
    birthElementSlug,
    dayElementSlug,
    branchRelation,
    elementRelation,
    focusArea,
    element_chemistry: elementChemistry,
    dateSeed,
    chartSeed,
  };
}

export function buildDailyReading({
  readingYear,
  readingMonth,
  readingDay,
  pillars,
  anchorSignName,
  czData,
  t,
  getSignLabel,
  getElementLabel,
}) {
  const focusPillar = pillars[2];
  const readingDayPillar = computeReadingDayPillar(readingYear, readingMonth, readingDay, czData);
  const readingDaySign = findSignProfile(czData, readingDayPillar.animal.name) || readingDayPillar.animal;
  const anchorSign = findSignProfile(czData, anchorSignName)
    || findSignProfile(czData, focusPillar?.animal?.name)
    || czData[0];
  const dateSeed = readingDateSeed(readingYear, readingMonth, readingDay);
  const chartSeed = birthChartSeed(pillars);
  const seed = dateSeed + (chartSeed * 97);

  const focusTraits = focusPillar?.animal?.traits?.length ? focusPillar.animal.traits : anchorSign.traits;
  const dayTraits = readingDaySign.traits?.length ? readingDaySign.traits : anchorSign.traits;

  const luckyPick = pickColorKey(
    seed + 5,
    anchorSign.luckyColorKeys,
    anchorSign.lucky,
  );
  const avoidPick = pickColorKey(
    seed + 17,
    anchorSign.avoidColorKeys,
    anchorSign.avoid,
  );

  const primaryTrait = deterministicPick(seed + 3, focusTraits);
  const secondaryTrait = deterministicPick(seed + 11, dayTraits);
  const guidanceTone = t(deterministicPick(seed, TONE_KEYS));

  const context = buildGuidanceContext({
    pillars,
    readingDayPillar,
    anchorSign,
    focusPillar,
    dateSeed,
    chartSeed,
    getSignLabel,
    getElementLabel,
    t,
  });
  context.trait = primaryTrait.toLowerCase();
  context.career_trait = secondaryTrait.toLowerCase();
  context.tone = guidanceTone;

  const { branchRelation, elementRelation, focusArea } = context;

  const summaryVariant = guidanceVariantIndex(dateSeed, chartSeed, "summary");
  const relVariant = guidanceVariantIndex(dateSeed, chartSeed, "relationships");
  const careerVariant = guidanceVariantIndex(dateSeed, chartSeed, "career");
  const wellnessVariant = guidanceVariantIndex(dateSeed, chartSeed, "wellness");

  const relationships = resolveGuidanceLine(
    t,
    `cz_daily_rel_${branchRelation}_${relVariant}`,
    context,
    "cz_daily_relationships",
  );
  const career = resolveGuidanceLine(
    t,
    `cz_daily_career_${focusArea}_${branchRelation}_${careerVariant}`,
    context,
    "cz_daily_career",
  );
  const wellness = resolveGuidanceLine(
    t,
    `cz_daily_wellness_${elementRelation}_${wellnessVariant}`,
    { element: context.reading_element },
    "cz_daily_wellness",
  );
  const summary = resolveGuidanceLine(
    t,
    `cz_daily_summary_${branchRelation}_${elementRelation}_${summaryVariant}`,
    context,
    "cz_daily_summary",
  );

  const doToday = resolveGuidanceLine(
    t,
    `cz_daily_do_${branchRelation}`,
    context,
    null,
  );
  const avoidToday = resolveGuidanceLine(
    t,
    `cz_daily_avoid_${branchRelation}`,
    context,
    null,
  );

  const dateIso = `${readingYear}-${String(readingMonth).padStart(2, "0")}-${String(readingDay).padStart(2, "0")}`;

  return {
    date: dateIso,
    summary,
    relationships,
    career,
    wellness,
    focusArea,
    branchRelation,
    elementRelation,
    whyLine: context.element_chemistry,
    doToday,
    avoidToday,
    luckyColor: luckyPick.label,
    luckyColorKey: luckyPick.key,
    avoidColor: avoidPick.label,
    avoidColorKey: avoidPick.key,
  };
}

function dominantElementsFromPillars(pillars) {
  const counts = pillars.reduce((memo, pillar) => {
    memo[pillar.element] = (memo[pillar.element] || 0) + 1;
    return memo;
  }, {});
  const orderedElements = ["Wood", "Fire", "Earth", "Metal", "Water"];
  const dominantElement = orderedElements.reduce(
    (best, element) => (counts[element] > counts[best] ? element : best),
    "Wood",
  );
  const weakElement = orderedElements.reduce(
    (best, element) => (counts[element] < counts[best] ? element : best),
    "Wood",
  );
  return { dominantElement, weakElement };
}

export function buildDestinySynthesisV2({
  pillars,
  readingYear,
  readingMonth,
  readingDay,
  czData,
  tr,
  getElementLabel,
  t,
}) {
  if (!pillars || pillars.length < 4) return null;
  const [yearPillar, monthPillar, dayPillar, hourPillar] = pillars;
  const { dominantElement, weakElement } = dominantElementsFromPillars(pillars);
  const readingDayPillar = computeReadingDayPillar(readingYear, readingMonth, readingDay, czData);
  const dateSeed = readingDateSeed(readingYear, readingMonth, readingDay);
  const chartSeed = birthChartSeed(pillars);
  const branchRelation = computeBranchRelation(dayPillar.animal.name, readingDayPillar.animal.name);
  const practicalVariant = guidanceVariantIndex(dateSeed, chartSeed, "synthesis");

  const readingAnimalLabel = readingDayPillar.animal.label || readingDayPillar.animal.name;
  const readingElementLabel = getElementLabel(readingDayPillar.element).toLowerCase();

  const anchorSign = findSignProfile(czData, dayPillar.animal.name) || dayPillar.animal;
  const compatSource = anchorSign?.compatible?.length ? anchorSign.compatible : [];
  const compatible1 = compatSource[chartSeed % compatSource.length] || compatSource[0] || "";

  const practicalGuidance = resolveGuidanceLine(
    t,
    `cz_synthesis_daily_${branchRelation}_${practicalVariant}`,
    {
      dominantAdvice: t(ELEMENT_BALANCE_KEYS[dominantElement]),
      weakAdvice: t(ELEMENT_BALANCE_KEYS[weakElement]),
      monthTrait: monthPillar.animal.traits[1].toLowerCase(),
      hourTrait: hourPillar.animal.traits[0].toLowerCase(),
      reading_animal: readingAnimalLabel,
      reading_element: readingElementLabel,
      dominantElement: getElementLabel(dominantElement).toLowerCase(),
      weakElement: getElementLabel(weakElement).toLowerCase(),
      dayAnimal: dayPillar.animal.label || dayPillar.animal.name,
      dayElement: getElementLabel(dayPillar.element).toLowerCase(),
      compatible1,
    },
    "cz_synthesis_v2_practical_guidance",
  );

  return {
    corePattern: tr("cz_synthesis_v2_core_pattern", {
      yearAnimal: yearPillar.animal.label || yearPillar.animal.name,
      yearElement: getElementLabel(yearPillar.element).toLowerCase(),
      yearTrait: yearPillar.animal.traits[0].toLowerCase(),
      dayAnimal: dayPillar.animal.label || dayPillar.animal.name,
      dayElement: getElementLabel(dayPillar.element).toLowerCase(),
      dayTrait1: dayPillar.animal.traits[2].toLowerCase(),
      dayTrait2: dayPillar.animal.traits[3].toLowerCase(),
    }),
    tensionStrength: tr("cz_synthesis_v2_tension_strength", {
      monthAnimal: monthPillar.animal.label || monthPillar.animal.name,
      monthElement: getElementLabel(monthPillar.element).toLowerCase(),
      hourAnimal: hourPillar.animal.label || hourPillar.animal.name,
      hourElement: getElementLabel(hourPillar.element).toLowerCase(),
      dominantElement: getElementLabel(dominantElement).toLowerCase(),
      weakElement: getElementLabel(weakElement).toLowerCase(),
    }),
    practicalGuidance,
    branchRelation,
  };
}

export function getHourAnimalIndex(hour) {
  return LEGACY_HOUR_ANIMALS[hour];
}

export { computeBranchRelation, computeElementRelation, pickDailyFocusArea, sectionVariantIndex };
